import { lazy } from "react";

import { AdminBootstrapLayout } from "@/components/templates";
import BranchStepsAdmin from "@/modules/BranchStepsAdmin/BranchStepsAdmin";
import BrandStepsAdmin from "@/modules/BrandStepsAdmin/BrandStepsAdmin";
import { AuthRole, StageAuth } from "@/store/AuthStore";

import AdminDefaultRedirect from "../AdminDefaultRedirect";
import { ADMINS, PATHS } from "../path";
import { ProtectedRoute } from "../ProtectedRoute";

const LayoutWithMenu = lazy(
  () => import("@/components/templates/LayoutWithMenu")
);
const AdminAccountManagement = lazy(
  () => import("@/modules/AdminAccountManagement/AdminAccountManagement")
);
const AdminCreateNewGroup = lazy(
  () => import("@/modules/AdminCreateNewGroup/AdminCreateNewGroup")
);
const AdminGroupDetailsPage = lazy(
  () => import("@/modules/AdminAccountManagement/AdminGroupDetailsPage")
);
const AdminBrandDetailsPage = lazy(
  () => import("@/modules/AdminAccountManagement/AdminBrandDetailsPage")
);
const AdminBranchDetailsPage = lazy(
  () => import("@/modules/AdminAccountManagement/AdminBranchDetailsPage")
);
const AdminUsersDetailsPage = lazy(
  () => import("@/modules/PortalUsers/AdminUsersDetailsPage")
);
const AdminGroupNewFunding = lazy(
  () => import("@/modules/AdminAccountManagement/AdminGroupNewFunding")
);

const AdminDinersPage = lazy(() => import("@/modules/AdminDiners/AdminDiners"));
const AdminDinersUserPage = lazy(
  () => import("@/modules/AdminDiners/AdminDinersUser")
);
const DashboardsReporting = lazy(
  () => import("@/modules/AdminReporting/DashboardsReporting")
);
const AdminOverview = lazy(
  () => import("@/modules/AdminOverview/AdminOverview")
);
const AdminTeamPrivatePage = lazy(
  () => import("@/modules/AdminTeam/AdminTeamPrivatePage")
);
const AdminAuditLog = lazy(
  () => import("@/modules/AdminAuditLog/AdminAuditLog")
);
const AdminTeamPage = lazy(() => import("@/modules/AdminTeam/AdminTeam"));
const SystemAppControl = lazy(
  () => import("@/modules/SystemAppControl/SystemAppControl")
);
const TransactionsReporting = lazy(
  () => import("@/modules/AdminReporting/TransactionsReporting")
);
const SystemTags = lazy(() => import("@/modules/SystemTags/SystemTags"));
const SystemBusinessRules = lazy(
  () => import("@/modules/SystemBusinessRules/SystemBusinessRules")
);
const SystemSettlements = lazy(
  () => import("@/modules/AdminReporting/SettlementsPages/Settlements")
);
const SystemAddNewCategory = lazy(
  () => import("@/components/units/SystemAddNewCategory/SystemCreateCategory")
);

const SystemUpdateCategory = lazy(
  () => import("@/components/units/SystemAddNewCategory/SystemEditCategory")
);

const BrandWizardSteps = lazy(
  () => import("@/modules/BrandStepsAdmin/BrandWizardWrapperSteps")
);

const BranchWizardSteps = lazy(
  () => import("@/modules/BranchStepsAdmin/BranchWizardWrapperSteps")
);
const ProfileSettings = lazy(
  () => import("@/modules/ProfileSettings/ProfileSettings")
);

const AdminNewLegalEntityPage = lazy(
  () => import("@/modules/AdminLegalEntityPages/AdminNewLegalEntityPage")
);

const LegalEntityPage = lazy(
  () => import("@/modules/AdminLegalEntityPages/LegalEntityPage")
);
const AdminOrderDetailsPage = lazy(
  () => import("@/modules/AdminOrderDetails/AdminOrderDetailsPage")
);

const AdminCreditBalanceReportPage = lazy(
  () => import("@/modules/AdminReporting/CreditBalanceReporting")
);
const AdminApplicationPage = lazy(
  () => import("@/modules/AdminApplicationPage/AdminApplicationPage")
);
const PortalUsersRolesPage = lazy(
  () => import("@/modules/PortalUsersRolesPage/PortalUsersRolesPage")
);
const AdminTeamRolesPage = lazy(
  () => import("@/modules/AdminTeamRoles/AdminTeamRoles")
);
const AdminIntegrationsPage = lazy(
  () => import("@/modules/AdminIntegrations/AdminIntegrations")
);
const AdminSystemTopUpsPage = lazy(
  () => import("@/modules/AdminReporting/TopUps")
);
const PortalUsersPage = lazy(() => import("@/modules/PortalUsers/PortalUsers"));

type StepConfig = {
  path: string;
  element: React.ReactNode;
};

const generateSteps = (
  paths: string[],
  component: React.ReactNode
): StepConfig[] => paths.map(path => ({ path, element: component }));

const BRAND_STEP_NAMES = {
  DETAILS: "details",
  MENU: "menu",
  CONTACTS: "contacts",
  BRANCHES: "branches",
};

const brandWizardBasePath = `${ADMINS.ACCOUNT}/brands/wizard`;
const brandWizardBasePathWithBrandId = `${ADMINS.ACCOUNT}/brands/wizard/:brandId`;

const brandWizardStepPaths = [
  BRAND_STEP_NAMES.DETAILS,
  BRAND_STEP_NAMES.MENU,
  BRAND_STEP_NAMES.CONTACTS,
  BRAND_STEP_NAMES.BRANCHES,
];

const brandWizardSteps = brandWizardStepPaths.map(step => ({
  path: step,
  element: <BrandWizardSteps />,
}));

const BRANCH_STEP_NAMES = {
  DETAILS: "details",
  HOURS: "business-hours",
  VIBES: "vibes",
  AMENITIES: "amenities",
  MEDIA: "media",
};

const branchWizardBasePath = `${ADMINS.ACCOUNT}/branches/wizard`;
const branchWizardBasePathWithBranchId = `${ADMINS.ACCOUNT}/branches/wizard/:branchId`;

const branchWizardStepPaths = [
  BRANCH_STEP_NAMES.DETAILS,
  BRANCH_STEP_NAMES.HOURS,
  BRANCH_STEP_NAMES.VIBES,
  BRANCH_STEP_NAMES.AMENITIES,
  BRANCH_STEP_NAMES.MEDIA,
];

const branchWizardSteps = branchWizardStepPaths.map(step => ({
  path: step,
  element: <BranchWizardSteps />,
}));

const newLegalEntity = generateSteps(
  [ADMINS.ACCOUNT_NEW_LEGAL_ENTITY],
  <AdminNewLegalEntityPage />
);

const legalEntity = generateSteps(
  [PATHS.ADMIN.ACCOUNT_LEGAL_ENTITY(":id")],
  <LegalEntityPage />
);

export const adminRoutes = [
  {
    element: <AdminBootstrapLayout />,
    children: [
      {
        path: PATHS.ADMIN.ROOT,
        element: (
          <ProtectedRoute
            requiredRole={[AuthRole.ADMIN]}
            requiredStage={StageAuth.AUTHENTICATED}
          >
            <LayoutWithMenu />
          </ProtectedRoute>
        ),
        children: [
          { path: "", element: <AdminDefaultRedirect /> },

          // Static admin pages
          { path: ADMINS.ACCOUNT, element: <AdminAccountManagement /> },
          { path: ADMINS.ACCOUNT_NEW_GROUP, element: <AdminCreateNewGroup /> },
          {
            path: ADMINS.ACCOUNT_GROUP_DETAILS(":id"),
            element: <AdminGroupDetailsPage />,
          },
          {
            path: ADMINS.ACCOUNT_GROUP_NEW_FUNDING(":id"),
            element: <AdminGroupNewFunding />,
          },
          {
            path: ADMINS.ACCOUNT_BRAND_DETAILS(":id"),
            element: <AdminBrandDetailsPage />,
          },
          {
            path: ADMINS.ACCOUNT_BRANCH(":id"),
            element: <AdminBranchDetailsPage />,
          },
          {
            path: ADMINS.ACCOUNT_BRANCH_TRANSACTIONS_DETAILS(
              ":id",
              ":transactionId"
            ),
            element: <AdminOrderDetailsPage />,
          },
          {
            path: ADMINS.PORTAL_USER_PAGE(":id"),
            element: <AdminUsersDetailsPage />,
          },
          { path: ADMINS.AUDIT, element: <AdminAuditLog /> },
          { path: ADMINS.DINERS, element: <AdminDinersPage /> },
          { path: ADMINS.DINERS_USER(":id"), element: <AdminDinersUserPage /> },
          {
            path: ADMINS.DINERS_USER_TRANSACTIONS(":id", ":transactionId"),
            element: <AdminOrderDetailsPage />,
          },
          { path: ADMINS.OVERVIEW, element: <AdminOverview /> },
          { path: ADMINS.REPORTING, element: <DashboardsReporting /> },
          {
            path: ADMINS.CREDIT_BALANCE_REPORTING,
            element: <AdminCreditBalanceReportPage />,
          },
          { path: ADMINS.ADMIN_TEAM, element: <AdminTeamPage /> },
          { path: ADMINS.ALL_PORTAL_USERS, element: <PortalUsersPage /> },
          { path: ADMINS.ADMIN_USERS_ROLES, element: <AdminTeamRolesPage /> },
          {
            path: ADMINS.USERS_PRIVATE(":id"),
            element: <AdminTeamPrivatePage />,
          },
          {
            path: PATHS.ADMIN.APPLICATION(":id"),
            element: <AdminApplicationPage />,
          },

          // System pages
          { path: ADMINS.PROFILE_SETTINGS, element: <ProfileSettings /> },
          {
            path: PATHS.ADMIN.SYSTEMS_APP_CONTROL,
            element: <SystemAppControl />,
          },
          {
            path: PATHS.ADMIN.SYSTEMS_TRANSACTIONS,
            element: <TransactionsReporting />,
          },
          {
            path: PATHS.ADMIN.SYSTEMS_TRANSACTIONS_DETAILS(":transactionId"),
            element: <AdminOrderDetailsPage />,
          },
          {
            path: PATHS.ADMIN.SYSTEMS_TOP_UPS,
            element: <AdminSystemTopUpsPage />,
          },
          {
            path: PATHS.ADMIN.SYSTEMS_NEW_COLLECTION,
            element: <SystemAddNewCategory />,
          },
          {
            path: PATHS.ADMIN.SYSTEMS_COLLECTION(":id"),
            element: <SystemUpdateCategory />,
          },
          {
            path: PATHS.ADMIN.SYSTEMS_PROMOTION,
            element: <AdminIntegrationsPage />,
          },
          {
            path: PATHS.ADMIN.SYSTEMS_MARKETING,
            element: <AdminIntegrationsPage />,
          },
          { path: PATHS.ADMIN.SYSTEM_TAGS, element: <SystemTags /> },
          { path: PATHS.ADMIN.USERS_ROLES, element: <PortalUsersRolesPage /> },
          {
            path: PATHS.ADMIN.SYSTEM_BUSINESS_RULES,
            element: <SystemBusinessRules />,
          },
          { path: PATHS.ADMIN.SYSTEM_PAYMENTS, element: <SystemSettlements /> },
          {
            path: PATHS.ADMIN.SYSTEM_EMAILS,
            element: <AdminIntegrationsPage />,
          },
          {
            path: brandWizardBasePath,
            element: <BrandStepsAdmin />,
            children: [...brandWizardSteps],
          },
          {
            path: brandWizardBasePathWithBrandId,
            element: <BrandStepsAdmin />,
            children: [...brandWizardSteps],
          },
          {
            path: branchWizardBasePath,
            element: <BranchStepsAdmin />,
            children: [...branchWizardSteps],
          },
          {
            path: branchWizardBasePathWithBranchId,
            element: <BranchStepsAdmin />,
            children: [...branchWizardSteps],
          },
          // Dynamic steps
          ...newLegalEntity,
          ...legalEntity,
        ],
      },
    ],
  },
];
